import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import moment from 'moment-timezone';
import refreshIcon from '../../../assets/images/refresh-purple.svg';
import DateRangeSelector from '../../../components/range-selector/DateRangeSelector';
import { useExternalAgencyPastShiftsQuery } from '../../../redux/externalAgencyShifts';
import { applyFilters } from '../../../utils/FilterFunctions';
import FiltersSection from './FiltersSection';
import {
	shiftTypeTemplate,
	rateTemplate,
	startDateTemplate,
	timePeriodTemplate,
	facilityTemplate,
	workedTimeRangeTemplate,
	buildFilterOptions,
	idBodyTemplate,
	idFieldTemplate,
	bundleFlagTemplate
} from './agency-utils';

const AgencyPastShifts = ({ searchTerm }) => {
	const PAST_SHIFTS_FILTER_OPTIONS = {
		facilities: [],
		shiftTypes: [],
		startTimes: []
	};

	const [filterOptions, setFilterOptions] = useState(PAST_SHIFTS_FILTER_OPTIONS);
	const [filters, setFilters] = useState(PAST_SHIFTS_FILTER_OPTIONS);
	const [globalFilter, setGlobalFilter] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
	const [rangeEnd, setRangeEnd] = useState(new Date());
	const [rangeStart, setRangeStart] = useState(moment(rangeEnd).subtract(13, 'day').toDate());
	const [shifts, setShifts] = useState([]);
	const [filteredBundleId, setFilteredBundleId] = useState(null);

	const { data: pastShifts, refetch: reloadPastShifts } = useExternalAgencyPastShiftsQuery(
		{ start: moment(rangeStart).format('YYYY-MM-DD'), end: moment(rangeEnd).format('YYYY-MM-DD') },
		{ refetchOnMountOrArgChange: true }
	);

	useEffect(() => {
		setRangeEnd(moment(rangeStart).add(13, 'day'));
	}, []);

	useEffect(() => {
		if (pastShifts) {
			const options = buildFilterOptions(pastShifts, filters);
			setFilterOptions(options);
		}
	}, [pastShifts]);

	useEffect(() => {
		let filteredShifts;
		if (filteredBundleId) {
			filteredShifts = pastShifts.filter((s) => s.contract_id === filteredBundleId);
		} else {
			filteredShifts = applyFilters({ items: pastShifts || [], filters });
		}
		setShifts(filteredShifts);
	}, [pastShifts, filters, filteredBundleId]);

	useEffect(() => {
		const _filter = { ...globalFilter };
		_filter.global.value = searchTerm;
		setGlobalFilter(_filter);
	}, [searchTerm]);

	const updateFilters = (updatedOptions, updatedFilters) => {
		setFilterOptions(updatedOptions);
		setFilters(updatedFilters);
	};

	const _idBodyTemplate = (item) => idBodyTemplate(item, setFilteredBundleId);
	const clearBundleFilter = () => setFilteredBundleId(null);

	return (
		<>
			<div className="toolbar">
				<DateRangeSelector
					rangeStart={rangeStart}
					rangeEnd={rangeEnd}
					setRangeStart={setRangeStart}
					setRangeEnd={setRangeEnd}
					maxDate={new Date()}
				/>

				<button type="button" className="btn refresh-button" onClick={reloadPastShifts}>
					<img src={refreshIcon} alt="Refresh" />
					<div className="button-label">Refresh Feed</div>
				</button>
				<div className="flex-one" />
				<FiltersSection
					filterOptions={filterOptions}
					filters={filters}
					updateFilters={updateFilters}
					isDisabled={!!globalFilter.global?.value || filteredBundleId}
				/>
			</div>

			<div className="datatable-wrapper">
				<div className="wrapper-container">
					<DataTable
						value={globalFilter.global?.value ? pastShifts : shifts}
						scrollable
						scrollHeight="flex"
						sortMode="multiple"
						virtualScrollerOptions={{ itemSize: 65 }}
						tableStyle={{ minWidth: '100rem' }}
						size="small"
						stripedRows
						cellClassName="table-cell"
						filters={globalFilter}
					>
						<Column
							field="start_time"
							header="Date"
							style={{ width: '13%' }}
							sortable
							body={(rowData) => startDateTemplate(rowData, 'past')}
						/>
						<Column field="start_time_formatted" header="" style={{ width: '0' }} hidden />
						<Column field="professional" header="Professional" sortable style={{ width: '12%' }} />
						<Column field="shift_type" header="Shift type" style={{ width: '8%' }} sortable body={shiftTypeTemplate} />
						<Column field="facility_management_company_name" header="Management" style={{ width: '15%' }} sortable />
						<Column field="facility_name" header="Facility" style={{ width: '14%' }} sortable body={facilityTemplate} />
						<Column field="unit" header="Unit" style={{ width: '10%' }} sortable />
						<Column field="start_hour_tz" header="Time" style={{ width: '12%' }} body={timePeriodTemplate} />
						<Column field="rate" header="Rate" style={{ width: '5%' }} sortable body={rateTemplate} />
						<Column
							field="adjusted_start_time"
							header="In/Out"
							style={{ width: '12%' }}
							body={workedTimeRangeTemplate}
						/>
						<Column field="break" header="Break" style={{ width: '5%' }} sortable />
						<Column
							field={bundleFlagTemplate}
							header="Bundle"
							style={{ width: '4%' }}
							sortable
							body={bundleFlagTemplate}
						/>
						<Column field={idFieldTemplate} header="ID" style={{ width: '5%' }} sortable body={_idBodyTemplate} />
					</DataTable>
				</div>
			</div>

			{filteredBundleId && !searchTerm && (
				<div className="footer">
					<div>Showing all shifts in the selected bundle within the applied date range</div>
					<button type="button" className="btn clear-button" onClick={clearBundleFilter}>
						See all past shifts
					</button>
				</div>
			)}
		</>
	);
};

export default AgencyPastShifts;
